import LINKS from 'src/_helpers/links'
import { DEFAULT_JOURNAL_DOMAIN_ROUTES } from 'src/_helpers/journalDomainRoutes'
import { getPreferredTranslation } from 'src/_helpers/getPreferredTranslation'
import { buildLocalizedPath, getEntityTranslationLocales, getLocalizedEntitySlug } from 'src/_helpers/localizedLinks'

export function buildJournalArticleUrl(
  article,
  locale,
  routeContext = 'main',
  journalRoutes = DEFAULT_JOURNAL_DOMAIN_ROUTES,
  // Language of the slugs; differs from the route locale on journal domains (e.g. /de/ shows English content).
  contentLocale = locale,
) {
  const articleTranslation = getPreferredTranslation(article, contentLocale) || {}
  const journalTranslation = article?.journal ? getPreferredTranslation(article.journal, contentLocale) : {}
  const issue = article?.issue || article?.parent || null
  const issueTranslation = issue ? getPreferredTranslation(issue, contentLocale) : {}
  const articleSlug = getLocalizedEntitySlug(article, contentLocale, {
    fallbackSlug: articleTranslation?.slug || article?.slug,
  })
  const journalSlug = getLocalizedEntitySlug(article?.journal, contentLocale, {
    fallbackSlug:
      journalTranslation?.slug ||
      article?.resolvedJournalSlug ||
      article?.journalSlugs?.[contentLocale] ||
      article?.journalSlug,
  })
  const issueSlug = getLocalizedEntitySlug(issue, contentLocale, {
    fallbackSlug:
      issueTranslation?.slug ||
      article?.resolvedIssueSlug ||
      article?.issueSlugs?.[contentLocale] ||
      article?.issueSlug,
  })

  const hasIssue = Boolean(
    issue || article?.parentUuid || article?.issueUuid || issueSlug || typeof article?.issue === 'number',
  )
  if (!hasIssue) {
    if (article?.volumeUuid || article?.volume) return null
    const standaloneSlug =
      article?.slugs?.[contentLocale] ||
      getPreferredTranslation(article, contentLocale, {
        fallbackLocale: null,
        includeDefaultLang: false,
        includeFirstTranslation: false,
      })?.slug ||
      (!article?.slugs && !article?.translations ? article?.slug : null)
    if (!standaloneSlug) {
      // Main-site lists can show publications in another language. Switch the route language too:
      // standalone slug lookup requires an exact language match. Journal domains resolve it separately.
      if (routeContext === 'journal-domain') return null

      const articleLocales = getEntityTranslationLocales(article, { requireSlug: true })
      const journalLocales = getEntityTranslationLocales(article?.journal || { slugs: article?.journalSlugs }, {
        requireSlug: true,
      })
      const defaultLocale = typeof article?.defaultLang === 'string' ? article.defaultLang : article?.defaultLang?.value
      const fallbackLocales = [...new Set([defaultLocale, 'en', ...articleLocales])].filter(
        (candidate) =>
          candidate !== contentLocale &&
          articleLocales.includes(candidate) &&
          (journalLocales.length === 0 || journalLocales.includes(candidate)),
      )

      for (const fallbackLocale of fallbackLocales) {
        const fallbackArticleSlug =
          article?.slugs?.[fallbackLocale] ||
          getPreferredTranslation(article, fallbackLocale, {
            fallbackLocale: null,
            includeDefaultLang: false,
            includeFirstTranslation: false,
          })?.slug
        const fallbackJournalSlug = getLocalizedEntitySlug(article?.journal, fallbackLocale, {
          fallbackSlug: article?.journalSlugs?.[fallbackLocale] || article?.journalSlug,
        })
        const path = buildLocalizedPath(LINKS.JOURNALS.ARTICLE, fallbackLocale, {
          slug: fallbackJournalSlug,
          article: fallbackArticleSlug,
        })
        if (path) return path
      }

      return null
    }

    return buildLocalizedPath(
      routeContext === 'journal-domain' ? journalRoutes.JOURNALS.ARTICLE : LINKS.JOURNALS.ARTICLE,
      locale,
      routeContext === 'journal-domain' ? { article: standaloneSlug } : { slug: journalSlug, article: standaloneSlug },
    )
  }

  const issueArticleRoute =
    routeContext === 'journal-domain' ? journalRoutes.ISSUES.DETAIL_DETAIL : LINKS.ISSUES.DETAIL_DETAIL

  return buildLocalizedPath(
    issueArticleRoute,
    locale,
    routeContext === 'journal-domain'
      ? {
          issue: issueSlug,
          article: articleSlug,
        }
      : {
          slug: journalSlug,
          issue: issueSlug,
          article: articleSlug,
        },
  )
}
